/**
 * Convert provider HTML bodies (Nylas) to plain text for routing and comments.
 */

import { looksLikeHtmlMarkup } from "../email/has_structured_email_html";
import { replaceHtmlAnchorTagsWithPlainLinks } from "./inbound_email_html_anchors_to_plain";

const HTML_TAG_RE = /<[^>]+>/g;
const BLOCK_CLOSE_TO_NEWLINE_RE = /<\/(p|div|tr|h[1-6]|li|table)\b[^>]*>/gi;
/** Inserts a boundary before common block opens so quoted headers often land on their own plain line. */
const BLOCK_OPEN_PREFIX_NEWLINE_RE = /<(div|p|li)\b[^>]*>/gi;
const BR_RE = /<br\s*\/?>/gi;

/** Blocks that must not become visible text when tags are stripped (Outlook VML lives in <style>). */
const SCRIPT_BLOCK_RE = /<script\b[^>]*>[\s\S]*?<\/script>/gi;
const NOSCRIPT_BLOCK_RE = /<noscript\b[^>]*>[\s\S]*?<\/noscript>/gi;
const STYLE_BLOCK_RE = /<style\b[^>]*>[\s\S]*?<\/style>/gi;
const HEAD_BLOCK_RE = /<head\b[^>]*>[\s\S]*?<\/head>/gi;
const IFRAME_BLOCK_RE = /<iframe\b[^>]*>[\s\S]*?<\/iframe>/gi;
const SVG_BLOCK_RE = /<svg\b[^>]*>[\s\S]*?<\/svg>/gi;
const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;
const CDATA_BLOCK_RE = /<!\[CDATA\[[\s\S]*?]]>/g;
const XML_DECL_RE = /<\?xml\b[\s\S]*?\?>/gi;
/** Malformed emails: style opened but not closed — strip tail so VML/CSS does not leak. */
const ORPHAN_STYLE_TO_EOF_RE = /<style\b[^>]*>[\s\S]+$/gi;

function stripNonVisibleHtmlRegions(html: string): string {
  let s: string = html;
  for (let i = 0; i < 3; i += 1) {
    const before: string = s;
    s = s
      .replace(XML_DECL_RE, "")
      .replace(SCRIPT_BLOCK_RE, "")
      .replace(NOSCRIPT_BLOCK_RE, "")
      .replace(STYLE_BLOCK_RE, "")
      .replace(HEAD_BLOCK_RE, "")
      .replace(IFRAME_BLOCK_RE, "")
      .replace(SVG_BLOCK_RE, "")
      .replace(HTML_COMMENT_RE, "")
      .replace(CDATA_BLOCK_RE, "");
    if (s === before) break;
  }
  s = s.replace(ORPHAN_STYLE_TO_EOF_RE, "");
  return s;
}

function decodeBasicEntities(text: string): string {
  return text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_m: string, code: string): string => {
      const n = Number.parseInt(code, 10);
      if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return _m;
      try {
        return String.fromCodePoint(n);
      } catch {
        return _m;
      }
    })
    .replace(/&#x([0-9a-f]+);/gi, (_m: string, hex: string): string => {
      const n = Number.parseInt(hex, 16);
      if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return _m;
      try {
        return String.fromCodePoint(n);
      } catch {
        return _m;
      }
    });
}

/** Maps CRLF, lone CR, LS, PS, NEL to LF so line-based heuristics behave consistently. */
export function normalizeEmailPlainLineBreaks(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/\u2028/g, "\n")
    .replace(/\u2029/g, "\n")
    .replace(/\u0085/g, "\n");
}

function collapseWhitespace(text: string): string {
  const normalized: string = normalizeEmailPlainLineBreaks(text);
  return normalized
    .split("\n")
    .map((line: string): string => line.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

export function looksLikeHtml(raw: string): boolean {
  return looksLikeHtmlMarkup(raw);
}

export function htmlToPlainText(html: string): string {
  let text: string = stripNonVisibleHtmlRegions(html);
  text = replaceHtmlAnchorTagsWithPlainLinks(text);
  text = text
    .replace(BR_RE, "\n")
    .replace(BLOCK_CLOSE_TO_NEWLINE_RE, "\n")
    .replace(BLOCK_OPEN_PREFIX_NEWLINE_RE, "\n$&");
  text = text.replace(HTML_TAG_RE, "");
  text = decodeBasicEntities(text);
  return collapseWhitespace(text);
}

export function emailRawToPlainText(raw: string): string {
  const trimmed: string = raw.trim();
  if (trimmed.length === 0) return "";
  if (looksLikeHtml(trimmed)) return htmlToPlainText(trimmed);
  return collapseWhitespace(trimmed);
}
